import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Pagination from '../ui/pagination';

const EMPTY_SELECTED_IDS: number[] = [];

interface MediaFile {
    id: number;
    name: string;
    title?: string;
    description?: string;
    path: string;
    type: string;
    folder: string;
    thumb_s?: string;
    thumb_m?: string;
    thumb_r?: string;
    url?: string;
}
interface MediaPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect?: (file: MediaFile) => void;
    onSelectMultiple?: (files: MediaFile[]) => void;
    onSelectMultipleIds?: (ids: number[]) => void;
    title?: string;
    multiple?: boolean;
    selectedIds?: number[];
}

export default function MediaPickerModal({
    isOpen,
    onClose,
    onSelect,
    onSelectMultiple,
    onSelectMultipleIds,
    title = 'Select Media',
    multiple = false,
    selectedIds = EMPTY_SELECTED_IDS
}: MediaPickerModalProps) {
    const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMediaIds, setSelectedMediaIds] = useState<number[]>([]);
    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(0);

    useEffect(() => {
        if (!isOpen) return;

        setSelectedMediaIds((prev) => {
            if (
                prev.length === selectedIds.length &&
                prev.every((id, index) => id === selectedIds[index])
            ) {
                return prev;
            }

            return selectedIds;
        });
    }, [isOpen, selectedIds]);

    useEffect(() => {
        if (isOpen) {

            const timer = setTimeout(() => {
                fetchData();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isOpen, searchTerm, currentPage, rowsPerPage]);
    const fetchData = async () => {
        setLoading(true);
        try {

            let res;
            const params = {
                page: currentPage,
                per_page: rowsPerPage,
                name: searchTerm || undefined
            };
            if (searchTerm) {
                res = await client.getApiMediaSearch(undefined, { params }) as any;
            } else {
                res = await client.getApiMediaFiles(undefined, { params }) as any;
            }
            const responseData = res.data;
            let files = [];
            let total = 0;
            if (responseData?.data?.data) {
                files = responseData.data.data;
                total = responseData.data.total;
            } else if (responseData?.data) {
                files = Array.isArray(responseData.data) ? responseData.data : (responseData.data.data || []);
                total = responseData.data.total || files.length;
            } else {
                files = Array.isArray(responseData) ? responseData : [];
                total = files.length;
            }
            setMediaFiles(Array.isArray(files) ? files : []);
            setTotalItems(total);
        } catch (err) {
            console.error('Failed to fetch media files:', err);
        } finally {
            setLoading(false);
        }
    };
    const getFullUrl = (url: string | undefined) => {
        if (!url) return undefined;
        if (url.startsWith('http')) return url;
        const baseUrl = import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000';
        return `${baseUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleFileClick = (file: MediaFile) => {
        if (!multiple) {
            onSelect?.(file);
            return;
        }

        setSelectedMediaIds((prev) =>
            prev.includes(file.id)
                ? prev.filter((id) => id !== file.id)
                : [...prev, file.id]
        );
    };

    const handleConfirmSelection = () => {
        if (!multiple) return;

        const selectedFiles = mediaFiles.filter((file) => selectedMediaIds.includes(file.id));
        onSelectMultiple?.(selectedFiles);
        onSelectMultipleIds?.(selectedMediaIds);
    };

    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-999999999999" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search files..."
                            value={searchTerm}

                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>
                <div className="p-6 overflow-y-auto flex-1">
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <span className="text-gray-500">Loading media...</span>
                        </div>

                    ) : mediaFiles.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500">No media files found.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">

                            {mediaFiles.map(file => (
                                <div
                                    key={file.id}
                                    onClick={() => handleFileClick(file)}
                                    className={`group relative bg-gray-50 dark:bg-gray-900 rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${selectedMediaIds.includes(file.id)
                                            ? 'border-brand-500 ring-2 ring-brand-500/20'
                                            : 'border-transparent hover:border-brand-500'
                                        }`}
                                >
                                    <div className="aspect-square flex items-center justify-center overflow-hidden">
                                        {file.type?.startsWith('image') ? (
                                            <img
                                                src={getFullUrl(file.path || file.thumb_m || file.url)}
                                                alt={file.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="p-2 text-center">
                                                <span className="text-2xl mb-1 block">📄</span>
                                                <span className="text-[10px] text-gray-500 uppercase truncate block w-full">{file.name}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="absolute inset-0 bg-brand-500/0 group-hover:bg-brand-500/10 transition-colors" />
                                    {multiple && selectedMediaIds.includes(file.id) && (
                                        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-500 text-white flex items-center justify-center shadow-md">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {totalItems > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-700">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(totalItems / rowsPerPage)}
                            onPageChange={setCurrentPage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(rows) => {
                                setRowsPerPage(rows);
                                setCurrentPage(1);
                            }}
                            totalItems={totalItems}
                        />
                    </div>
                )}
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                    {multiple && (
                        <button
                            onClick={handleConfirmSelection}
                            disabled={selectedMediaIds.length === 0}
                            className="px-4 py-2 mr-3 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Add Selected ({selectedMediaIds.length})
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
